import { gql } from '@apollo/client'
import React from 'react'

import { accountsClient } from '../../../../utils/apollo/api'
import { useQuery } from '../../../../utils/apollo/_helpers/useQuery'
import { useServiceOrder } from './ServiceOrderLoader'

/**
 * Campo Adicional do EXECUTOR da atividade (a conta/usuário que executou o serviço —
 * `serviceOrder.user`, o mesmo campo já usado em todo o app para "Resp. pela realização
 * da visita técnica"/"quem executou a atividade", ver ex.:
 * `bioqualynet/pgr/sections/01.CompanyInfoSection.js` e
 * `serviceOrders/executionV2/ExecutionForm.js`). Requisito de Julia Buzzo (PM, 2026-08-27):
 * o Certificado de Calibração da Seção 21 não pode vir das respostas da própria atividade —
 * tem que vir de um Campo Adicional cadastrado na CONTA do usuário executor.
 *
 * Consulta separada contra `accountsClient` (a API de Contas, diferente da API de Ordens de
 * Serviço usada por `ServiceOrderLoader.js`) porque é ali que vive o Campo Adicional de
 * usuário — mesmo client e mesmo formato de query já usados (e em produção) pelo autofill de
 * formulário de execução (`serviceOrders/executionV2/_requests/fetchAutofillValue.js`,
 * `USER_FIELD_VALUES_QUERY`/`mergeUserFieldValues`): um Campo Adicional de usuário pode viver
 * tanto em `user.fieldValues` quanto em `user.contract.fieldValues`, dependendo de como foi
 * configurado — por isso ambos são consultados e mesclados aqui, do mesmo jeito.
 *
 * Sem `serviceOrder.user` (atividade sem executor atribuído — ex.: resposta anônima via
 * link público, ver `serviceOrder?.user === null` em `ServiceOrderHeader.js` de outros
 * módulos) a query é pulada (`skip`) e o contexto expõe uma lista vazia — nunca lança erro
 * nem impede o restante do PDF de renderizar (ver `getExecutorCertificadoCalibracao` em
 * `pgr/_helpers/certificadoEngine.js`, que já trata lista vazia/campo ausente como "sem
 * certificado, omitir a imagem").
 */
const Context = React.createContext([])
const { Provider } = Context
export const useExecutorFieldValues = () => React.useContext(Context)

const QUERY = gql`
  query ExecutorFieldValuesForCertificadoCalibracao($id: ID) {
    user(id: $id, filter: { includeDisabled: true, ignoreSegments: true }) {
      id
      contract {
        fieldValues {
          id
          value
          field {
            id
            name
            type
          }
        }
      }
      fieldValues {
        id
        value
        field {
          id
          name
          type
        }
      }
    }
  }
`

const ExecutorCertificadoLoader = ({ children }) => {
  const serviceOrder = useServiceOrder()
  const executorId = serviceOrder?.user?.id

  const { data } = useQuery(QUERY, {
    client: accountsClient,
    skip: !executorId,
    variables: { id: executorId }
  })

  const fieldValues = [...(data?.user?.contract?.fieldValues ?? []), ...(data?.user?.fieldValues ?? [])]

  return <Provider value={fieldValues}>{children}</Provider>
}

export default ExecutorCertificadoLoader
